import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  Button,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShareIcon from '@mui/icons-material/Share';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import InlineVoiceInput from '../components/InlineVoiceInput';
import { PageShell } from '../components/layout';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';

const features = [
  {
    icon: <SearchIcon />,
    title: "Find Homes",
    example: "I'm looking for a 4-bedroom house in Toronto under $1.5M"
  },
  {
    icon: <AssessmentIcon />,
    title: "Evaluate My Home",
    example: "What's my house worth in Richmond Hill?"
  },
  {
    icon: <CampaignIcon />,
    title: "Marketing Strategy",
    example: "Create a marketing plan for my condo"
  },
  {
    icon: <HomeWorkIcon />,
    title: "Seller Preparation",
    example: "How to prepare my house for sale"
  },
  {
    icon: <ShareIcon />,
    title: "List My Home",
    example: "List my property on MLS"
  },
  {
    icon: <TrendingUpIcon />,
    title: "Market Analysis",
    example: "How's the Toronto market?"
  }
];

const Home = () => {
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isLoggedIn = !!currentUser;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);

  // Capture outreach tracking code (`?ref=...`) into sessionStorage so signup
  // and property-creation flows can attribute the conversion. The redirect
  // from /api/outreach/r/<code> lands here with ?ref=<code>&utm_*.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (ref) {
        window.sessionStorage.setItem('outreach_ref', ref.toUpperCase());
        const source = params.get('utm_source');
        const medium = params.get('utm_medium');
        const campaign = params.get('utm_campaign');
        if (source || medium || campaign) {
          window.sessionStorage.setItem(
            'outreach_utm',
            JSON.stringify({ source, medium, campaign }),
          );
        }
      }
    } catch (_) {
      // sessionStorage may be disabled (private mode); ignore silently.
    }
  }, []);
  
  const handleSearch = () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    analytics.homeSearch(trimmedQuery);

    // Navigate to ChatPage with the query in both state and URL.
    // URL fallback makes the handoff resilient if navigation state is unavailable.
    navigate(`/chat?q=${encodeURIComponent(trimmedQuery)}`, {
      state: { initialQuery: trimmedQuery }
    });
  };

  /**
   * Handle voice input transcript
   */
  const handleVoiceTranscript = (transcript, isFinal = true) => {
    if (transcript.trim()) {
      if (isFinal) {
        // Final transcript - append to existing query or replace if empty
        const newQuery = query.trim() 
          ? `${query} ${transcript}` 
          : transcript;
        setQuery(newQuery);
      } else {
        // Interim transcript - show live preview but don't save yet
        const previewQuery = query.trim() 
          ? `${query} ${transcript}` 
          : transcript;
        setQuery(previewQuery);
      }
    }
  };

  /**
   * Handle voice recording state changes
   */
  const handleVoiceRecordingChange = (recording) => {
    setIsVoiceRecording(recording);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  return (
    <PageShell
      kind="tool"
      footer
      dots
      decoration={(
        <Box
          component="svg"
          viewBox="0 0 1200 2000"
          preserveAspectRatio="none"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            pointerEvents: 'none',
            '& path': {
              fill: 'none',
              strokeWidth: 2.5,
              opacity: 0.08,
              strokeLinecap: 'round',
              strokeDasharray: '200 100',
              animation: 'along 30s linear infinite, breathe 60s ease-in-out infinite',
            },
            '& path:nth-of-type(3n+1)': {
              animationDuration: '36s, 72s',
            },
            '& path:nth-of-type(3n+2)': {
              animationDuration: '45s, 90s',
            },
            '& path:nth-of-type(2n)': {
              animationDirection: 'reverse, normal',
            },
            '@keyframes along': {
              to: { 
                strokeDashoffset: -2000 
              },
            },
            '@keyframes breathe': {
              '0%, 100%': { 
                strokeWidth: 2.5 
              },
              '50%': { 
                strokeWidth: 2.875 
              },
            },
          }}
        >
          {/* Gradient definitions for fading effect */}
          <defs>
            <linearGradient id="fadeBlue" x1="0%" y1="0%" x2="100%" y2="5%">
              <stop offset="0%" stopColor="#1e40af" stopOpacity="0" />
              <stop offset="15%" stopColor="#1e40af" stopOpacity="1" />
              <stop offset="85%" stopColor="#1e40af" stopOpacity="1" />
              <stop offset="80%" stopColor="#1e40af" stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id="fadePurple" x1="0%" y1="0%" x2="100%" y2="5%">
              <stop offset="5%" stopColor="#7c3aed" stopOpacity="0" />
              <stop offset="15%" stopColor="#7c3aed" stopOpacity="1" />
              <stop offset="85%" stopColor="#7c3aed" stopOpacity="1" />
              <stop offset="70%" stopColor="#7c3aed" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient id="fadePink" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="5%" stopColor="#db2777" stopOpacity="0" />
              <stop offset="10%" stopColor="#db2777" stopOpacity="1" />
              <stop offset="75%" stopColor="#db2777" stopOpacity="1" />
              <stop offset="100%" stopColor="#db2777" stopOpacity="0.6" />
            </linearGradient>
          </defs>
        
          <path stroke="url(#fadePink)" d="M0,780  C520,0     1130,1440  1200,100" />
          {/* <path stroke="url(#fadePurple)" d="M0,900  C560,80    1140,1560  1200,250" /> */}
          <path stroke="url(#fadeBlue)" d="M0,1020 C600,160   1180,1680  1200,450" />
          <path stroke="url(#fadePink)" d="M0,1140 C640,240   1200,1800  1200,600" />
          {/* <path stroke="url(#fadePurple)" d="M0,1260 C680,320   1200,1920  1200,800" /> */}
          <path stroke="url(#fadeBlue)" d="M0,1380 C720,400   1200,2040  1200,1000" />
          {/* <path stroke="url(#fadePink)" d="M0,1500 C760,480   1200,2160  1200,1200" /> */}
          {/* <path stroke="url(#fadePurple)" d="M0,1620 C800,560   1200,2280  1200,1500" /> */}
          {/* <path stroke="url(#fadeBlue)" d="M0,1740 C840,640   1200,2400  1200,1800" />
          <path stroke="url(#fadePink)" d="M0,1860 C880,720   1200,2520  1200,1950" /> */}
        </Box>
      )}
    >
      {/* Main Content Section */}
      <Box
        sx={{
          flex: '0 0 auto',
          minHeight: {
            xs: 'calc(100vh - 80px)',
            sm: 'calc(100vh - 90px)',
            md: 'calc(100vh - 100px)',
          },
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: 2, sm: 3, md: 2 }, // Reduced padding on desktop
          pb: { xs: 4, sm: 6, md: 2 }, // Reduced bottom padding on desktop
          position: 'relative',
          zIndex: 5,
          overflow: 'visible',
        }}
      >
        <Container 
          maxWidth="md" 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            flex: { xs: 'none', md: 1 }, // Don't flex on mobile, flex on desktop
            px: { xs: 2, sm: 3 }, // Responsive horizontal padding
            mb: { xs: 3, sm: 4, md: 1 }, // Reduced bottom margin on desktop
            height: { md: '100%' }, // Full height on desktop
          }}
        >
          {/* Title and subtitle section */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: { xs: 'flex-start', md: 'center' }, // Center on desktop, start on mobile
            flex: { md: 1 }, // Take available space on desktop
            mb: { xs: 3, sm: 4, md: 2 }, // Reduced margin on desktop
            mt: { xs: 2, sm: 3, md: 1 }, // Reduced top margin on desktop
            textAlign: 'center',
            px: { xs: 2, sm: 2 }, // Increased horizontal padding for mobile
          }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: { xs: 3, sm: 3, md: 4 }, // Increased bottom margin
                textAlign: 'center',
                fontSize: {
                  xs: 'clamp(1.8rem, 10vw, 2.5rem)', // Larger on mobile, increased viewport width sensitivity
                  sm: 'clamp(2.2rem, 6vw, 3.2rem)', // Medium on tablet
                  md: 'clamp(2.7rem, 5vw, 3.6rem)', // Full size on desktop
                },
                lineHeight: { xs: 1.2, sm: 1.2, md: 1.3 }, // Better line height
                letterSpacing: { xs: '-0.01em', sm: '-0.02em' }, // Adjusted letter spacing
                fontWeight: { xs: 700, sm: 600, md: 600 }, // Bolder on mobile
                px: { xs: 0, sm: 0 }, // Remove horizontal padding that might cause issues
                // Enhanced gradient text styling with fallbacks
                background: theme.modern.colors.gradient.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                // Fallback for browsers that don't support gradient text
                color: theme.modern.colors.modern.mainColor[600],
                '@supports not (background-clip: text)': {
                  color: theme.modern.colors.modern.mainColor[600],
                },
                // Ensure visibility on mobile
                display: 'block',
                width: '100%',
              }}
            >
              KeyTa Real Estate Assistant
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 4, sm: 5, md: 6 }, // Better responsive margins
                textAlign: 'center',
                maxWidth: { xs: '100%', sm: '650px' },
                mx: 'auto',
                color: theme.modern.colors.modern.slate[500],
                fontSize: {
                  xs: 'clamp(1rem, 4vw, 1.1rem)', // Smaller on mobile
                  sm: 'clamp(1.1rem, 3vw, 1.2rem)', // Medium on tablet
                  md: 'clamp(1.2rem, 2vw, 1.25rem)', // Full size on desktop
                },
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                letterSpacing: '0.01em',
                fontWeight: 400,
                px: { xs: 2, sm: 1, md: 0 }, // Horizontal padding for mobile
              }}
            >
              Ask the KeyTa assistant to help you sell or buy a home, or to explain your local market.
            </Typography>           
          </Box>

          {/* Chat input section */}
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1000px', 
            mx: 'auto', 
            mb: { xs: 4, sm: 5, md: 3 }, // Reduced bottom margin on desktop
            px: { xs: 1, sm: 0 }, // Horizontal padding for mobile
          }}>
            <Paper
              variant="modernInput"
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 }, // Responsive padding
                borderRadius: { xs: 2, sm: 3 }, // Responsive border radius
                '&:hover': { 
                  borderColor: theme.modern.colors.gradient.primary,
                },
              }}
            >
              <Stack direction="row" alignItems="center">
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="What would you like to know about real estate?"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: { xs: '1rem', sm: '1.2rem' }, // Responsive font size
                      lineHeight: 1.8,
                      color: theme.modern.colors.modern.slate[600],
                      fontWeight: 400,
                      '&::placeholder': {
                        color: theme.modern.colors.modern.slate[400],
                        opacity: 1,
                      }
                    }
                  }}
                  sx={{
                    '& textarea': {
                      resize: 'none',
                      height: '24px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                    },
                  }}
                />
                
                {/* Voice Input Button */}
                <InlineVoiceInput
                  onTranscript={handleVoiceTranscript}
                  onListeningChange={(listening) => {
                    // Optional: Handle listening state changes
                    console.log('Voice listening:', listening);
                  }}
                  onRecordingStateChange={handleVoiceRecordingChange}
                  analyticsSource="home"
                  disabled={false}
                  size="medium"
                />
                
                {/* Send Button - Hidden when recording */}
                {!isVoiceRecording && (
                  <IconButton
                    onClick={handleSearch}
                    disabled={!query.trim()}
                    size="medium"
                    sx={{
                      color: query.trim() ? 'primary.main' : 'rgba(0, 0, 0, 0.3)',
                      ml: 1,
                      width: '49px',
                      height: '49px',
                      '&:hover': { 
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    <SendIcon fontSize="medium" />
                  </IconButton>
                )}
              </Stack>
            </Paper>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                textAlign: 'center', 
                mt: 1, 
                color: 'rgba(0, 0, 0, 0.5)' 
              }}
            >
              {/* AI-powered assistant. May produce inaccurate information about properties. */}
            </Typography>
          </Box>
        </Container>

        {/* Examples Section */}
        <Container maxWidth="md" sx={{ 
          py: { xs: 3, sm: 4, md: 2 }, // Reduced padding on desktop
          px: { xs: 2, sm: 3 }, // Horizontal padding for mobile
          flex: { xs: '0 0 auto', md: '0 0 auto' }, // Don't grow on any screen size
          overflow: 'visible',
        }}>
          <Typography
            variant="h3"
            sx={{
              mb: { xs: 3, sm: 4, md: 2 }, // Reduced margin on desktop
              textAlign: 'center',
              fontWeight: 700,
              fontSize: {
                xs: 'clamp(1.4rem, 6vw, 1.8rem)', // Smaller on mobile
                sm: 'clamp(1.6rem, 4vw, 2rem)', // Medium on tablet
                md: 'clamp(1.5rem, 2vw, 1.8rem)', // More compact on desktop
              },
              letterSpacing: '-0.01em',
              color: theme.modern.colors.modern.slate[800],
              px: { xs: 1, sm: 0 }, // Horizontal padding for mobile
            }}
          >
            We <span style={theme.modern.utils.gradientText('primary')}>Simplify</span> Real Estate
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3, md: 2 }} sx={{ px: { xs: 0, sm: 1 },animation: 'revealUp 650ms ease forwards' }}>
            {features.map((feature, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  variant="modern"
                  onClick={() => { analytics.homeFeatureChipClick(feature.title); setQuery(feature.example); }}
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ 
                    p: { xs: 2, sm: 2.5, md: 2 }, // More compact padding on desktop
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <Box
                      sx={{
                        color: theme.modern.colors.modern.mainColor[500],
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      {feature.icon}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: theme.modern.colors.modern.slate[700],
                          fontSize: { xs: '1rem', sm: '1.1rem' }, // Responsive font size
                          lineHeight: 1.3,
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2"
                      sx={{
                        color: theme.modern.colors.modern.slate[500],
                        fontSize: { xs: '0.9rem', sm: '0.95rem' }, // Responsive font size
                        lineHeight: { xs: 1.3, sm: 1.4 },
                        fontStyle: 'italic',
                        mt: 'auto', // Push to bottom of card
                      }}
                    >
                      "{feature.example}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </PageShell>
  );
};

export default Home;

